import { Pagination } from "@shopify/polaris";
import moment from "moment";
import React from "react";

export const SUPPORT_TABLE_PAGE_SIZE = 10;

export const displayText = (value, fallback = "Unknown") => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  return String(value);
};

export const displayCount = (value) => {
  if (value === null || value === undefined) return "Unknown";
  const count =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+$/.test(value.trim())
      ? Number(value.trim())
      : Number.NaN;
  if (!Number.isSafeInteger(count) || count < 0) return "Unknown";
  return count.toLocaleString();
};

export const displayBoolean = (value, trueLabel, falseLabel) => {
  if (value === true) return trueLabel;
  if (value === false) return falseLabel;
  return "Unknown";
};

export const displayDateTime = (
  value,
  { includeTime = true, fallback = "Not recorded" } = {}
) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return fallback;
  }
  const date = moment(value);
  if (!date.isValid()) return "Unknown";
  return date.format(includeTime ? "MMM DD, YYYY, h:mm A" : "MMM DD, YYYY");
};

const nonNegativeInteger = (value) => {
  if (
    value === null ||
    value === undefined ||
    typeof value === "boolean" ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

export const ReadOnlyTablePagination = ({
  response,
  currentPage,
  rowCount,
  noun,
  pluralNoun = `${noun}s`,
  onPageChange,
}) => {
  const totalElements = nonNegativeInteger(response?.totalElements);
  const responsePage = nonNegativeInteger(response?.pageNo);
  const responsePageSize = nonNegativeInteger(response?.pageSize);
  const totalPages = nonNegativeInteger(response?.totalPages);
  const page = responsePage ?? nonNegativeInteger(currentPage) ?? 0;
  const pageSize = responsePageSize || SUPPORT_TABLE_PAGE_SIZE;
  const safeRowCount = nonNegativeInteger(rowCount) ?? 0;
  const firstResult = safeRowCount === 0 ? 0 : page * pageSize + 1;
  const lastResult = safeRowCount === 0 ? 0 : firstResult + safeRowCount - 1;
  const resultLabel =
    totalElements === null
      ? `${safeRowCount.toLocaleString()} ${
          safeRowCount === 1 ? noun : pluralNoun
        } on this page`
      : totalElements === 0
      ? `0 ${pluralNoun}`
      : `Showing ${firstResult.toLocaleString()}–${Math.min(
          lastResult,
          totalElements
        ).toLocaleString()} of ${totalElements.toLocaleString()} ${
          totalElements === 1 ? noun : pluralNoun
        }`;
  const hasPrevious = page > 0;
  const hasNext =
    typeof response?.last === "boolean"
      ? !response.last
      : totalPages !== null
      ? page + 1 < totalPages
      : safeRowCount === pageSize;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "12px",
        padding: "16px",
        borderTop: "1px solid #e1e3e5",
      }}
    >
      <p aria-live="polite" style={{ color: "#6d7175", margin: 0 }}>
        {resultLabel}
      </p>
      {hasPrevious || hasNext ? (
        <Pagination
          hasPrevious={hasPrevious}
          onPrevious={() => onPageChange(Math.max(0, page - 1))}
          hasNext={hasNext}
          onNext={() => onPageChange(page + 1)}
          label={
            totalPages !== null && totalPages > 0
              ? `Page ${page + 1} of ${totalPages}`
              : `Page ${page + 1}`
          }
        />
      ) : null}
    </div>
  );
};
